import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, ArrowRight, MapPin } from 'lucide-react';
import { collection, getDocs, orderBy, query, startAfter, limit, type QueryDocumentSnapshot, type DocumentData } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { formatDateForDisplay } from '../utils/dateUtils';
import Reveal from './Reveal';
import LazyFirebaseImage from './LazyFirebaseImage';

interface Activity {
  id: string;
  title: string;
  date: string;
  content: string;
  imageUrl: string;
    location?: string;
}

const ActivitiesDetail: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const location = useLocation();
  const state = (location && (location as any).state) as { from?: 'home' } | null;
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const pageSize = 12;

  const loadFirstPage = async () => {
    setIsLoaded(false);
    const activitiesCollection = collection(db, 'activities');
    const baseQuery = query(activitiesCollection, orderBy('date', 'desc'), limit(pageSize));
    const querySnapshot = await getDocs(baseQuery);
    const docs = querySnapshot.docs;
    const activitiesList = await Promise.all(
      docs.map(async (doc) => {
        const data = doc.data();
        const rawImage = typeof data.imageUrl === 'string' ? data.imageUrl : '';
        return {
          id: doc.id,
          title: data.title,
          date: data.date,
          content: data.content,
          imageUrl: rawImage,
          location: data.location || '',
        } as Activity;
      })
    );
    setActivities(activitiesList);
    setHasNext(docs.length === pageSize);
    setLastDoc(docs.length > 0 ? docs[docs.length - 1] : null);
    setIsLoaded(true);
  };

  const handleLoadMore = async () => {
    if (!hasNext || !lastDoc) return;
    setIsLoadingMore(true);
    const activitiesCollection = collection(db, 'activities');
    const moreQuery = query(
      activitiesCollection,
      orderBy('date', 'desc'),
      startAfter(lastDoc),
      limit(pageSize)
    );
    const querySnapshot = await getDocs(moreQuery);
    const docs = querySnapshot.docs;
    const moreList = await Promise.all(
      docs.map(async (doc) => {
        const data = doc.data();
        const rawImage = typeof data.imageUrl === 'string' ? data.imageUrl : '';
        return {
          id: doc.id,
          title: data.title,
          date: data.date,
          content: data.content,
          imageUrl: rawImage,
          location: data.location || '',
        } as Activity;
      })
    );
    setActivities((prev) => [...prev, ...moreList]);
    setHasNext(docs.length === pageSize);
    setLastDoc(docs.length > 0 ? docs[docs.length - 1] : lastDoc);
    setIsLoadingMore(false);
  };

  useEffect(() => {
    // 初期ロード
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardClick = (activityId: string) => {
    navigate(`/activities-detail/${activityId}`,
      { state: { from: 'activities-list' as const } }
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white" style={{ marginTop: '80px' }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/#activities', { state: { instant: true } })}
              className="flex items-center text-pink-500 hover:text-pink-600 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold" style={{ color: '#e3628d' }}>
              活動報告
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      {/* Activities Content */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* カードグリッド（塊で同時に表示） */}
            {isLoaded && (
            <Reveal duration={1.2} delay={0.3}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1 flex flex-col h-full"
                    onClick={() => handleCardClick(activity.id)}
                  >
                    {/* 画像 */}
                    <div className="relative aspect-[16/9] rounded-t-lg bg-gray-50 overflow-hidden">
                      <LazyFirebaseImage
                        pathOrUrl={activity.imageUrl}
                        alt={activity.title}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                        className="w-full h-full object-cover"
                      />
                      {/* カテゴリ表示はデータにあれば将来復活 */}
                    </div>

                    {/* カード内容 */}
                    <div className="p-4 flex flex-col flex-1">
                      <div className="min-h-[3.5rem] mb-2 flex items-center">
                        <h3 className="text-lg font-bold text-gray-800 line-clamp-2">
                          {activity.title}
                        </h3>
                      </div>
                      
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{formatDateForDisplay(new Date(activity.date))}</span>
                        </div>
                        {activity.location && (
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{activity.location}</span>
                          </div>
                        )}
                      </div>

                      <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-grow">
                        {activity.content.substring(0, 50)}...
                      </p>

                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-pink-500 text-sm font-medium">
                          詳細を見る
                        </span>
                        <ArrowRight className="w-4 h-4 text-pink-500" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            )}
            {/* さらに表示（ある場合のみ） */}
            {isLoaded && hasNext && (
              <div className="flex items-center justify-center mt-6">
                <button
                  onClick={handleLoadMore}
                  className="text-base font-medium text-pink-600 bg-pink-50 hover:bg-pink-100 px-4 py-2 rounded-full border border-pink-200 transition-colors disabled:opacity-50"
                  aria-label="さらに表示"
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? '読み込み中…' : '↓ さらに表示'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivitiesDetail; 